import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    container: {
        padding: 10,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 15,
        color: '#1e40af',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        paddingBottom: 5,
    },
    subsectionTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginTop: 15,
        marginBottom: 10,
        color: '#374151',
        backgroundColor: '#f3f4f6',
        padding: 8,
    },
    table: {
        display: 'flex',
        width: '100%',
        marginBottom: 15,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
        paddingVertical: 8,
    },
    tableHeader: {
        backgroundColor: '#f9fafb',
        fontWeight: 'bold',
    },
    labelCell: {
        width: '40%',
        paddingRight: 10,
        fontSize: 11,
        fontWeight: 'bold',
        color: '#374151',
    },
    valueCell: {
        width: '60%',
        fontSize: 11,
        color: '#111827',
    },
    required: {
        color: '#dc2626',
    },
});

interface DefineStagePDFProps {
    data: {
        project_name: string;
        brand_name: string;
        location_name: string;
        customer: string;
        application_name: string;
        primary_package_type: string;
        category: string;
        net_contents: string;
        net_contents_unit: string;
        filled_top_load?: string;
        filled_top_load_unit?: string;
        primary_package_diameter: string;
        primary_package_diameter_unit: string;
        primary_package_height: string;
        primary_package_height_unit: string;
        bottle_design?: string;
        has_tertiary_package: string;
        items_per_case: string;
        pack_per_case?: string;
        pallet_base_type: string;
    };
}

export const DefineStagePDF: React.FC<DefineStagePDFProps> = ({ data }) => {
    const formatValue = (value: string | undefined, unit?: string) => {
        if (!value) return 'N/A';
        return unit ? `${value} ${unit}` : value;
    };

    const formatPackageType = (type: string) => {
        if (type === 'pet_bottles') return 'PET Bottles';
        if (type === 'cans') return 'Cans';
        return type;
    };

    return (
        <View style={styles.container}>
            <Text style={styles.sectionTitle}>Project Definition</Text>

            {/* Project Details */}
            <Text style={styles.subsectionTitle}>Project Details</Text>
            <View style={styles.table}>
                {/* <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.labelCell}>Field</Text>
                    <Text style={styles.valueCell}>Value</Text>
                </View> */}

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Project Name <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.project_name || 'N/A'}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Brand <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.brand_name || 'N/A'}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Location <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.location_name || 'N/A'}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Customer <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.customer || 'N/A'}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Application <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.application_name || 'N/A'}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Primary Package Type <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{formatPackageType(data.primary_package_type) || 'N/A'}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Category <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.category || 'N/A'}</Text>
                </View>
            </View>

            {/* Package Details */}
            <Text style={styles.subsectionTitle}>Package Details</Text>
            <View style={styles.table}>
                {/* <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.labelCell}>Field</Text>
                    <Text style={styles.valueCell}>Value</Text>
                </View> */}

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Net Contents <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{formatValue(data.net_contents, data.net_contents_unit)}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Filled Top Load</Text>
                    <Text style={styles.valueCell}>{formatValue(data.filled_top_load, data.filled_top_load_unit)}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Primary Package Diameter <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{formatValue(data.primary_package_diameter, data.primary_package_diameter_unit)}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Primary Package Height <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{formatValue(data.primary_package_height, data.primary_package_height_unit)}</Text>
                </View>

                {data.primary_package_type === 'pet_bottles' && (
                    <View style={styles.tableRow}>
                        <Text style={styles.labelCell}>Bottle Design <Text style={styles.required}>*</Text></Text>
                        <Text style={styles.valueCell}>{data.bottle_design || 'N/A'}</Text>
                    </View>
                )}
            </View>

            {/* Shipper Details */}
            <Text style={styles.subsectionTitle}>Shipper Details</Text>
            <View style={styles.table}>
                {/* <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.labelCell}>Field</Text>
                    <Text style={styles.valueCell}>Value</Text>
                </View> */}

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Has Tertiary Package <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.has_tertiary_package === 'yes' ? 'Yes' : 'No'}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Items / Case</Text>
                    <Text style={styles.valueCell}>{data.items_per_case || 'N/A'}</Text>
                </View>

                {data.has_tertiary_package === 'yes' && (
                    <View style={styles.tableRow}>
                        <Text style={styles.labelCell}>Pack / Case</Text>
                        <Text style={styles.valueCell}>{data.pack_per_case || 'N/A'}</Text>
                    </View>
                )}
            </View>

            {/* Pallet Details */}
            <Text style={styles.subsectionTitle}>Pallet Details</Text>
            <View style={styles.table}>
                {/* <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.labelCell}>Field</Text>
                    <Text style={styles.valueCell}>Value</Text>
                </View> */}

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Pallet Base Type <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.pallet_base_type || 'N/A'}</Text>
                </View>
            </View>
        </View>
    );
}; 